import React, { useState } from 'react';
import API_BASE_URL from '../config';
import useSafeBack from '../hooks/useSafeBack';
import '../styles/Contact.css';

const initialForm = {
  name: '',
  email: '',
  subject: 'general',
  message: '',
};

function Contact() {
  const safeBack = useSafeBack('/');
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errorMsg) setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setErrorMsg('Please fill in all required fields.');
      return;
    }

    setStatus('sending');
    setErrorMsg('');

    try {
      const res = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          subject: form.subject,
          message: form.message.trim(),
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.detail || 'Failed to send message. Please try again.');
        setStatus('error');
        return;
      }

      setStatus('sent');
      setForm(initialForm);
    } catch (err) {
      setErrorMsg('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-page__bg" aria-hidden />
      <div className="sa-page contact-page__inner">
        <div className="sa-container">
          <div className="contact-page__headerRow">
            <button
              type="button"
              className="contact-page__back"
              onClick={safeBack}
              aria-label="Go back"
            >
              ← Back
            </button>
          </div>

          <header className="contact-hero">
            <div className="contact-hero__accent" aria-hidden />
            <div className="contact-hero__content">
              <p className="contact-hero__eyebrow">We’re here to help</p>
              <h1 className="contact-hero__title">Contact SkillArea</h1>
              <p className="contact-hero__lead">
                Questions about hiring, becoming a technician, or partnerships? Send us a note —
                our team typically replies within one business day.
              </p>
            </div>
          </header>

          <div className="contact-layout">
            <aside className="contact-aside">
              <div className="contact-card contact-card--highlight">
                <div className="contact-card__icon" aria-hidden>
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
                    <path
                      d="M4 6h16v12H4V6z"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4 7l8 6 8-6"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h2 className="contact-card__title">Email</h2>
                <p className="contact-card__text">
                  For general inquiries and support.
                </p>
                <a className="contact-card__link" href="mailto:info@skillarea.com">
                  info@skillarea.com
                </a>
              </div>

              <div className="contact-card">
                <div className="contact-card__icon contact-card__icon--muted" aria-hidden>
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
                    <path
                      d="M12 7v6l4 2"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <h2 className="contact-card__title">Response time</h2>
                <p className="contact-card__text">
                  Monday–Friday, 9am–6pm local time. Weekend messages are queued for Monday.
                </p>
              </div>

              <div className="contact-card">
                <div className="contact-card__icon contact-card__icon--muted" aria-hidden>
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
                    <path
                      d="M12 21s7-4.5 7-11a7 7 0 10-14 0c0 6.5 7 11 7 11z"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.6" />
                  </svg>
                </div>
                <h2 className="contact-card__title">Head office</h2>
                <p className="contact-card__text contact-card__text--address">
                  SkillArea HQ
                  <br />
                  Canada (placeholder)
                </p>
              </div>
            </aside>

            <section className="contact-formWrap" aria-labelledby="contact-form-heading">
              <div className="contact-formShell">
                {status === 'sent' ? (
                  <div className="contact-success" role="status">
                    <div className="contact-success__check" aria-hidden>
                      <svg viewBox="0 0 24 24" width="48" height="48" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                        <path
                          d="M8 12.5l2.5 2.5L16 9"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <h2 id="contact-form-heading" className="contact-success__title">
                      Message sent
                    </h2>
                    <p className="contact-success__text">
                      Thanks for reaching out. We’ve received your message and will get back to you
                      soon at the email you provided.
                    </p>
                    <button
                      type="button"
                      className="sa-btn sa-btn--primary contact-success__btn"
                      onClick={() => setStatus('idle')}
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 id="contact-form-heading" className="contact-formShell__title">
                      Send us a message
                    </h2>
                    <p className="contact-formShell__subtitle">
                      Fill in the form below. Fields marked with <span className="contact-req">*</span>{' '}
                      are required.
                    </p>

                    <form className="contact-form" onSubmit={handleSubmit} noValidate>
                      <div className="contact-form__row">
                        <label className="contact-field">
                          <span className="contact-field__label">
                            Name <span className="contact-req">*</span>
                          </span>
                          <input
                            className="sa-input contact-field__input"
                            name="name"
                            type="text"
                            autoComplete="name"
                            required
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Your full name"
                          />
                        </label>
                        <label className="contact-field">
                          <span className="contact-field__label">
                            Email <span className="contact-req">*</span>
                          </span>
                          <input
                            className="sa-input contact-field__input"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={form.email}
                            onChange={handleChange}
                            placeholder="you@example.com"
                          />
                        </label>
                      </div>

                      <label className="contact-field">
                        <span className="contact-field__label">Subject</span>
                        <div className="contact-selectWrap">
                          <select
                            className="sa-input contact-field__input contact-select"
                            name="subject"
                            value={form.subject}
                            onChange={handleChange}
                          >
                            <option value="general">General question</option>
                            <option value="hirer">Hiring / client account</option>
                            <option value="technician">Technician onboarding</option>
                            <option value="billing">Billing &amp; payments</option>
                            <option value="partnership">Partnership / press</option>
                          </select>
                        </div>
                      </label>

                      <label className="contact-field">
                        <span className="contact-field__label">
                          Message <span className="contact-req">*</span>
                        </span>
                        <textarea
                          className="sa-input contact-field__input contact-textarea"
                          name="message"
                          required
                          rows={5}
                          value={form.message}
                          onChange={handleChange}
                          placeholder="Tell us how we can help…"
                        />
                      </label>

                      {errorMsg && (
                        <p className="contact-form__error" role="alert">
                          {errorMsg}
                        </p>
                      )}

                      <div className="contact-form__actions">
                        <button
                          type="submit"
                          className="sa-btn sa-btn--primary contact-submit"
                          disabled={status === 'sending'}
                        >
                          {status === 'sending' ? 'Sending…' : 'Send message'}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
